import debounce from 'lodash/debounce'
import { string, node } from 'prop-types'
import { createContext } from 'react'
import { allowedEvents } from './events'
import { track } from '.'

function trackEvent(eventName = '', ...args) {
  if (!allowedEvents.includes(eventName.toLowerCase())) {
    console.warn(
      `${eventName} is not one of the trackable event strings: ${allowedEvents.join(
        ', '
      )}`
    )
  }
  track(eventName.toLowerCase(), {
    ...args,
    trackingContext: this.stack,
  })
}

const debouncedFunc = debounce(trackEvent, 1000)

function debouncedTrackEvent(...args) {
  debouncedFunc.call(this, ...args)
}

/**
 * * WITHOUT ARGS: Instantiated on app load with initial value (called globaly on app load)
 * * WITH ARGS: Rebuild with new stack including additional context (called from AnalyticsLayer)
 *
 * @param {string[]} [prevStack]
 * @param {string} context
 * @deprecated Switch to `<TrackActionProvider>` and `useTrackAction`.
 * @see https://workwithopal.atlassian.net/wiki/spaces/ET/pages/3317596201/Segment+Event+Tracking
 */
const buildTracker = (prevStack, context) => {
  // Build or rebuild stack in new closure
  const tracker = {
    stack: !prevStack ? ['Opal'] : [...prevStack, context],
  }

  // Bind or rebind track functionality to maintain access to a current `this.track` in `trackEvent`
  tracker.track = trackEvent.bind(tracker)
  // TODO: remove debouncedTrack
  tracker.debouncedTrack = debouncedTrackEvent.bind(tracker)
  allowedEvents.forEach((event) => {
    tracker[event] = (...args) => tracker.track(event, ...args)
  })
  return tracker
}

const tracker = buildTracker()

/**
 * @deprecated Switch to `<TrackActionProvider>` and `useTrackAction`.
 * @see https://workwithopal.atlassian.net/wiki/spaces/ET/pages/3317596201/Segment+Event+Tracking
 */
const analyticsContext = createContext(tracker)
analyticsContext.displayName = 'Quanta: AnalyticsLayer ← analyticsContext'
const { Provider, Consumer } = analyticsContext

/**
 * @deprecated Switch to `<TrackActionProvider>` and `useTrackAction`.
 * @see https://workwithopal.atlassian.net/wiki/spaces/ET/pages/3317596201/Segment+Event+Tracking
 */
const AnalyticsLayer = ({ name, children }) => (
  <Consumer>
    {({ stack }) => {
      const localTracker = buildTracker(stack, name)
      return <Provider value={localTracker}>{children}</Provider>
    }}
  </Consumer>
)
AnalyticsLayer.displayName = 'Quanta: AnalyticsLayer'

AnalyticsLayer.propTypes = {
  children: node,
  name: string,
}

export default analyticsContext

export {
  analyticsContext,
  AnalyticsLayer,
  Consumer as AnalyticsConsumer,
  buildTracker,
}
